"use client";

import { Events } from "@/src/config";
import { User } from "@/src/store/type";
import { clientAccessTokenCookieOptions } from "@/src/util/access-token-cookie";
import { Code, DubboError } from "@apachedubbo/dubbo";
import { deleteCookie, getCookie, setCookie } from "cookies-next/client";
import * as jose from "jose";
import { publish } from "./event";

let sessionInvalidated = false;
let refreshPromise: Promise<boolean> | null = null;
type RefreshSuccessHandler = (accessToken: string) => void;
let refreshSuccessHandler: RefreshSuccessHandler | null = null;

export function registerAuthRefreshHandler(handler: RefreshSuccessHandler): void {
  refreshSuccessHandler = handler;
}

export function isAuthSessionInvalidated(): boolean {
  return sessionInvalidated;
}

export function resetAuthSessionInvalidation(): void {
  sessionInvalidated = false;
  refreshPromise = null;
}

export function isAccessTokenExpired(token: string): boolean {
  try {
    const { exp } = jose.decodeJwt(token);
    if (typeof exp !== "number") {
      return false;
    }
    return Date.now() >= exp * 1000;
  } catch {
    return true;
  }
}

export function invalidateAuthSession(messageKey = "error.auth.session_expired"): void {
  if (typeof window === "undefined" || sessionInvalidated) {
    return;
  }
  sessionInvalidated = true;
  refreshPromise = null;
  deleteCookie("access-token");
  publish(Events.InvalidToken, { message: messageKey });
}

export function throwSessionExpiredError(): never {
  throw new DubboError("error.auth.session_expired", Code.Unauthenticated);
}

export function failAuthSession(messageKey = "error.auth.session_expired"): never {
  if (!sessionInvalidated) {
    invalidateAuthSession(messageKey);
  }
  throwSessionExpiredError();
}

function applyRefreshedAccessToken(accessToken: string): boolean {
  try {
    jose.decodeJwt(accessToken) as User;
    setCookie("access-token", accessToken, clientAccessTokenCookieOptions(accessToken));
    refreshSuccessHandler?.(accessToken);
    return true;
  } catch (err) {
    console.error("[Auth] invalid token after refresh", err);
    return false;
  }
}

/** Single-flight refresh — concurrent API calls share one refresh request. */
export function refreshAccessToken(): Promise<boolean> {
  if (sessionInvalidated) {
    return Promise.resolve(false);
  }
  if (refreshPromise) {
    return refreshPromise;
  }

  refreshPromise = (async (): Promise<boolean> => {
    try {
      const url =
        typeof window !== "undefined"
          ? `${window.location.origin}/api/auth/refresh-token`
          : "/api/auth/refresh-token";
      const res = await fetch(url, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: "{}",
      });

      let data: { success?: boolean; accessToken?: string; message?: string } = {};
      try {
        data = await res.json();
      } catch {
        // ignore parse error
      }

      if (res.ok && data.success && data.accessToken) {
        return applyRefreshedAccessToken(data.accessToken);
      }

      if (!res.ok) {
        console.error("[Auth] Refresh failed:", res.status, data.message ?? data);
      }
      return false;
    } catch (error) {
      console.error("[Auth] Token refresh failed:", error);
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
}

/** Token if the session can supply one. Never invalidates the session. */
export async function optionalAccessToken(): Promise<string | undefined> {
  if (sessionInvalidated) {
    return undefined;
  }

  let token = getCookie("access-token") as string | undefined;
  if (token && !isAccessTokenExpired(token)) {
    return token;
  }

  if (token && isAccessTokenExpired(token)) {
    deleteCookie("access-token");
  }

  const refreshed = await refreshAccessToken();
  if (!refreshed) {
    return undefined;
  }

  token = getCookie("access-token") as string | undefined;
  if (token && !isAccessTokenExpired(token)) {
    return token;
  }
  return undefined;
}

export async function ensureValidAccessToken(): Promise<string> {
  const token = await optionalAccessToken();
  if (token) {
    return token;
  }
  failAuthSession("error.auth.session_expired");
}
